import { useState, useEffect } from 'react';
import { Undo, Redo, ZoomIn, ZoomOut, Save, Share2, MousePointer2, Hand, RotateCcw, Eye, EyeOff, Loader2, FileDown } from 'lucide-react';
import { useConfiguratorStore } from '@store/useConfiguratorStore';
import { useToast } from './ToastProvider';
import ShareModal from './ShareModal';
import HDRISelector from '@components/Scene/HDRISelector';
import RecordingOverlay from './RecordingOverlay';
import VideoPreviewModal from './VideoPreviewModal';
import './FloatingToolbar.css';

declare global {
    interface Window {
        _sceneRecordVideo?: () => void;
        _sceneOnRecordComplete?: (blob: Blob) => Promise<void>;
        _sceneRecordProgress?: number;
        _sceneReset?: () => void;
        _sceneZoomIn?: () => void;
        _sceneZoomOut?: () => void;
        _sceneCaptureMultiAngle?: (faceCount: number, options?: { mode?: 'psd' | 'proof', isWireframe?: boolean, isArtworkOnly?: boolean }) => Promise<HTMLCanvasElement[]>;
    }
}

export default function FloatingToolbar() {
    const {
        undo, redo, canUndo, canRedo,
        toolMode, setToolMode,
        showPrintAreaBorders, togglePrintAreaBorders,
        lightingPreset, setLightingPreset,
        sceneBackground, setSceneBackground,
        canZoomIn, canZoomOut,
        setActiveLogoInstance, setActiveTextInstance,
        glbUrl,
        isRecording,
        currentModel,
    } = useConfiguratorStore();

    // Local processing states
    const [isProcessing, setIsProcessing] = useState(false);
    const [isPSDProcessing, setIsPSDProcessing] = useState(false);
    const [isEPSProcessing, setIsEPSProcessing] = useState(false);
    const [isSVGProcessing, setIsSVGProcessing] = useState(false);
    const [showShare, setShowShare] = useState(false);
    const [recordingProgress, setRecordingProgress] = useState(0);
    const [videoPreview, setVideoPreview] = useState<{ blob: Blob, videoUrl: string } | null>(null);

    const { showToast } = useToast();

    // Save PNG directly
    const handleSavePNG = () => {
        try {
            const canvas = document.querySelector('canvas');
            if (!canvas) {
                alert('Unable to capture canvas. Please try again.');
                return;
            }

            // Create a temporary canvas to composite the background
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = canvas.width;
            tempCanvas.height = canvas.height;
            const ctx = tempCanvas.getContext('2d');

            if (!ctx) return;

            // 1. Fill with the Scene Background Color (Little Grey)
            ctx.fillStyle = '#E5E7EB'; // Visibly "Little Grey" (Tailwind Gray-200)
            ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

            // 2. Draw the 3D Canvas on top
            ctx.drawImage(canvas, 0, 0);

            const link = document.createElement('a');
            link.download = `mockup-${Date.now()}.png`;
            link.href = tempCanvas.toDataURL('image/png');
            link.click();
        } catch (error) {
            console.error('Save failed:', error);
            alert('Failed to save image. Please try again.');
        }
    };

    // Record Video - Uses main canvas for accurate recording
    const handleRecordVideo = async () => {
        if (isProcessing || isRecording) return; // Prevent multiple clicks

        if (!window._sceneRecordVideo) {
            showToast('Recording not available. Please wait for scene to load.', 'error');
            return;
        }

        setIsProcessing(true);
        setRecordingProgress(0);

        // Setup completion handler
        window._sceneOnRecordComplete = async (blob: Blob) => {

            setRecordingProgress(100);

            try {
                // Step 1: Upload to Object Storage
                // showToast('Uploading to Object Storage...', 'info');

                const { uploadVideoToStorage, isStorageConfigured } = await import('@/services/linodeStorage');

                if (!isStorageConfigured()) {
                    showToast('Storage not configured. Check your .env file.', 'error');
                    setIsProcessing(false);
                    return;
                }

                const videoUrl = await uploadVideoToStorage(blob);
                // showToast('Video uploaded successfully!', 'success');

                // Instead of auto-sending email, open the video preview modal
                setVideoPreview({ blob, videoUrl });

            } catch (error: any) {
                console.error('Video processing failed:', error);
                showToast(`Error: ${error.message || 'Processing failed'}`, 'error');
            } finally {
                setIsProcessing(false);
                setRecordingProgress(0);
                window._sceneOnRecordComplete = undefined;
            }
        };

        // Start recording
        window._sceneRecordVideo();
    };

    // Download Template Proof (PDF)
    const handleDownloadProof = async () => {
        if (isProcessing || isRecording || isPSDProcessing) return;

        if (!window._sceneCaptureMultiAngle || !window._sceneReset) {
            showToast('Proof capture not available. Please wait.', 'error');
            return;
        }

        setIsProcessing(true); // Re-use processing state or add a new one
        showToast('Generating Proof... This takes a moment.', 'info');

        // Store current state to restore later
        const originalColor = (useConfiguratorStore.getState().modelParts[0] as any)?.color || '#ffffff';

        try {
            await new Promise(resolve => setTimeout(resolve, 50));

            // Capture Main Views (Proof Mode ensures Ortho + Wireframe internally)
            const { trimCanvas } = await import('@/utils/canvasUtils');
            const mainCanvases = await window._sceneCaptureMultiAngle(4, { mode: 'proof', isWireframe: true });

            if (mainCanvases.length < 4) {
                showToast('Failed to capture proof views.', 'error');
                setIsProcessing(false);
                return;
            }

            const mainCaptures = mainCanvases.map((c, i) => {
                // All captures are now horizontally pre-rotated by the Scene component.
                // No manual rotation needed.
                const trimmed = trimCanvas(c);
                return {
                    image: trimmed.toDataURL('image/png'),
                    label: `Side ${i + 1}`,
                };
            });

            // 2. Capture Variants (White Barrel + Colored Grip/Tip)
            const variantColors = [
                { name: 'Pink', hex: '#EC4899' },
                { name: 'Cyan', hex: '#06B6D4' },
                { name: 'Green', hex: '#22C55E' },
                { name: 'Red', hex: '#EF4444' },
                { name: 'Navy', hex: '#1E3A8A' },
                { name: 'Black', hex: '#000000' },
            ];

            const variantCaptures = [];

            // Parts that stay White (Barrel, Tip, Clip)
            const whiteParts = ['Body', 'Cap', 'Top', 'Tip', 'Clip'];
            // Parts that get the Color (Grip only) - try multiple names to be safe
            const colorParts = ['Grip', 'Grip 1', 'Grip_1', 'Rubber', 'RubberGrip'];

            for (const color of variantColors) {


                // set Barrel White
                whiteParts.forEach(part => useConfiguratorStore.getState().updatePartColor(part, '#ffffff'));

                // set Grip/Tip Color
                colorParts.forEach(part => useConfiguratorStore.getState().updatePartColor(part, color.hex));

                await new Promise(resolve => setTimeout(resolve, 500));

                const variantCanvasList = await window._sceneCaptureMultiAngle(1, { mode: 'proof', isWireframe: false });
                if (variantCanvasList[0]) {
                    const trimmed = trimCanvas(variantCanvasList[0]);
                    // Variant capture (faceCount=1) is ALWAYS Horizontal (index 0 is last of 1).
                    // So DO NOT ROTATE.
                    variantCaptures.push({
                        image: trimmed.toDataURL('image/png'),
                        color: color.name,
                    });
                }
            }

            // 3. Generate PDF
            const { generateTemplatePDF, downloadPDF } = await import('@/utils/templateGenerator');

            const pdfBytes = await generateTemplatePDF({
                mainCaptures,
                variantCaptures,
            });

            downloadPDF(pdfBytes, `proof-template-${Date.now()}.pdf`);
            showToast('Proof PDF downloaded!', 'success');

        } catch (error: any) {
            console.error('Proof generation failed:', error);
            showToast('Failed to generate proof.', 'error');
        } finally {
            // Restore State
            useConfiguratorStore.getState().updateAllColors(originalColor);
            setIsProcessing(false);
        }
    };

    // Download PSD - Dynamic for any product
    const handleDownloadPSD = async () => {
        if (isPSDProcessing || isRecording) return;

        if (!window._sceneCaptureMultiAngle || !window._sceneReset) {
            showToast('PSD capture not available. Please wait for the scene to load.', 'error');
            return;
        }

        // Get product config
        const productId = useConfiguratorStore.getState().currentModel;
        const { getPSDConfig } = await import('@/config/psdConfigs');
        const psdConfig = getPSDConfig(productId);

        if (!psdConfig) {
            showToast('PSD generation is not available for this product.', 'info');
            return;
        }

        setIsPSDProcessing(true);
        showToast('Generating PSD... This may take a few seconds.', 'info');

        // Store current state to restore later
        const originalColor = (useConfiguratorStore.getState().modelParts[0] as any)?.color || '#ffffff';

        try {
            await new Promise(resolve => setTimeout(resolve, 50));

            // 1. Capture angles (count driven by config)
            const captures = await window._sceneCaptureMultiAngle(psdConfig.captureCount, { mode: 'psd' });

            if (captures.length < psdConfig.captureCount) {
                showToast('Failed to capture product views. Please try again.', 'error');
                setIsPSDProcessing(false);
                return;
            }

            // 2. Capture Variants (driven by config)
            await import('@/utils/canvasUtils');

            const modelVariants: { captures: HTMLCanvasElement[]; label: string }[] = [];

            for (const color of psdConfig.variantColors) {


                // Set fixed parts to white
                psdConfig.variantParts.fixed.forEach(part =>
                    useConfiguratorStore.getState().updatePartColor(part, '#ffffff')
                );
                // Set variable parts to variant color
                psdConfig.variantParts.variable.forEach(part =>
                    useConfiguratorStore.getState().updatePartColor(part, color.hex)
                );

                await new Promise(resolve => setTimeout(resolve, 500));

                const variantCapturesList = await window._sceneCaptureMultiAngle(
                    psdConfig.captureCount,
                    { mode: 'psd', isWireframe: false }
                );
                if (variantCapturesList.length === psdConfig.captureCount) {
                    modelVariants.push({
                        captures: variantCapturesList,
                        label: color.name,
                    });
                }
            }

            // 3. Collect Print Area Canvases — split if configured
            const printAreas = useConfiguratorStore.getState().printAreas;
            const printAreaCanvases = new Map<string, HTMLCanvasElement>();

            if (window._getPrintAreaCanvas) {
                // Check if 'all-faces' exists to enable filtering
                const hasAllFaces = printAreas.some(p => p.id === 'all-faces');

                for (const area of printAreas) {
                    const canvas = window._getPrintAreaCanvas(area.id);
                    if (!canvas) continue;

                    // Check if this print area needs splitting into face strips
                    const splitCount = psdConfig.printAreaSplitting?.[area.id];

                    if (splitCount && splitCount > 1) {
                        const faceWidth = Math.floor(canvas.width / splitCount);
                        const faceHeight = canvas.height;
                        for (let f = 0; f < splitCount; f++) {
                            const faceCanvas = document.createElement('canvas');
                            faceCanvas.width = faceWidth;
                            faceCanvas.height = faceHeight;
                            const ctx = faceCanvas.getContext('2d')!;
                            ctx.drawImage(
                                canvas,
                                f * faceWidth, 0, faceWidth, faceHeight,
                                0, 0, faceWidth, faceHeight
                            );
                            // Rename to 'face_X' to match user expectation (1-based index)
                            printAreaCanvases.set(`face_${f + 1}`, faceCanvas);

                        }
                    } else {
                        // If we processed 'all-faces', skip individual faces to avoid duplicates
                        if (hasAllFaces && (
                            area.id.includes('face') ||
                            area.id.includes('side') ||
                            area.id === 'front' ||
                            area.id === 'back' ||
                            area.id === 'left' ||
                            area.id === 'right'
                        )) {

                            continue;
                        }

                        printAreaCanvases.set(area.id, canvas);

                    }
                }
            }

            // 4. Generate PSD
            const { generatePSD, downloadPSD } = await import('@/utils/psdGenerator');

            // Map results to ModelCapture (including corners)
            const modelCaptures = captures.map((canvas, i) => ({
                canvas: canvas,
                label: `Angle ${i + 1}`,
            }));

            const psdBuffer = generatePSD({
                width: psdConfig.dimensions.width,
                height: psdConfig.dimensions.height,
                productName: productId,
                captures: modelCaptures,
                printAreaCanvases,
                variants: modelVariants,
                horizontalCaptures: psdConfig.horizontalCaptures,
            });

            downloadPSD(psdBuffer, `${productId}-mockup-${Date.now()}.psd`);
            showToast('PSD downloaded successfully!', 'success');
        } catch (error: any) {
            console.error('[PSD] Generation failed:', error);
            showToast(`PSD generation failed: ${error.message || 'Unknown error'}`, 'error');
        } finally {
            // Restore State
            useConfiguratorStore.getState().updateAllColors(originalColor);
            setIsPSDProcessing(false);
        }
    };

    // Download Package (ZIP) containing Artwork EPS files and product images
    const handleDownloadEPS = async () => {
        if (isEPSProcessing || isPSDProcessing || isRecording) return;

        if (!window._sceneCaptureMultiAngle || !window._sceneReset) {
            showToast('Capture not available. Please wait for the scene to load.', 'error');
            return;
        }

        const productId = useConfiguratorStore.getState().currentModel;
        const { getPSDConfig } = await import('@/config/psdConfigs');
        const psdConfig = getPSDConfig(productId);

        if (!psdConfig) {
            showToast('Package generation is not available for this product.', 'info');
            return;
        }

        setIsEPSProcessing(true);
        showToast('Generating Package (ZIP)... This may take a few seconds.', 'info');

        const originalColor = (useConfiguratorStore.getState().modelParts[0] as any)?.color || '#ffffff';

        try {
            await new Promise(resolve => setTimeout(resolve, 50));

            const captures = await window._sceneCaptureMultiAngle(psdConfig.captureCount, { mode: 'psd' });

            if (captures.length < psdConfig.captureCount) {
                showToast('Failed to capture product views. Please try again.', 'error');
                setIsEPSProcessing(false);
                return;
            }

            const JSZipModule = await import('jszip');
            const JSZip = JSZipModule.default || JSZipModule;
            const zip = new JSZip();

            // Collect Print Area Canvases and add them to ZIP as EPS artwork files
            const printAreas = useConfiguratorStore.getState().printAreas;

            if (productId === 'square-pen' && window._getPrintAreaCanvas) {
                // SQUARE PEN: Independent, 1:1 Distortion-Free Face Capture
                // Simply grab the exact 2D canvas of each face and export it 1:1. 
                // Do NOT resize it into physical proportions here, as that squashes the logos.
                const { generateArtworkEPS } = await import('@/utils/artworkEpsGenerator');
                
                // Dynamically get the exact Face print areas (ignoring the 'All Faces' wrapper)
                const facePrintAreas = printAreas.filter(p => 
                    p.id !== 'all-faces' && 
                    !p.id.toLowerCase().includes('body-ends') // safety filter for endcaps if any
                );
                
                for (let f = 0; f < facePrintAreas.length; f++) {
                    const printArea = facePrintAreas[f];
                    const faceId = printArea.id;
                    
                    // 1. Get the pure 2D individual face canvas (1024x1024 generally)
                    const faceCanvas = window._getPrintAreaCanvas(faceId);
                    
                    if (faceCanvas && printArea.uvBounds) {
                        const { minU, maxU, minV, maxV } = printArea.uvBounds;
                        
                        // The printArea typically only uses a fraction of the 1024x1024 texture.
                        // We must crop the EPS exactly to the used UV bounds to avoid the logo looking "tiny".
                        const sourceWidth = faceCanvas.width;
                        const sourceHeight = faceCanvas.height;
                        
                        // Handle wrapping (modulo 1) for safe math
                        const safeModulo = (val: number) => {
                            const m = val % 1;
                            if (m < 0 && m > -0.001) return 0;
                            return (m + 1) % 1;
                        };
                        
                        const uvWidth = maxU - minU > 0.99 ? 1 : maxU - minU;
                        const uvHeight = maxV - minV > 0.99 ? 1 : maxV - minV;
                        
                        const left = safeModulo(minU) * sourceWidth;
                        const top = (1 - safeModulo(maxV)) * sourceHeight; // 3D UV is bottom-up, Canvas is top-down
                        const width = uvWidth * sourceWidth;
                        const height = uvHeight * sourceHeight;
                        
                        const exportScale = 8; // High resolution multiplier
                        const finalWidth = width * exportScale;
                        const finalHeight = height * exportScale;

                        // For the Square Pen (and most pens), the user designs horizontally, 
                        // but the texture wraps vertically around the shaft. 
                        // We must rotate the cropped piece -90 degrees and flip it to match the user's flat UI perception.
                        const exportedCanvas = document.createElement('canvas');
                        exportedCanvas.width = finalHeight; // Swapped for 90 deg rotation
                        exportedCanvas.height = finalWidth;
                        const ctx = exportedCanvas.getContext('2d')!;
                        
                        // Fill with the user's selected model color instead of forced white
                        ctx.fillStyle = originalColor;
                        ctx.fillRect(0, 0, exportedCanvas.width, exportedCanvas.height);
                        
                        // Move to center to perform rotation
                        ctx.translate(exportedCanvas.width / 2, exportedCanvas.height / 2);
                        
                        // 1. Un-rotate the 90 degrees that the UI added
                        ctx.rotate(-90 * Math.PI / 180);
                        
                        // 2. Fix the mirror effect
                        // The raw canvas was natively drawn with scale(1, -1) for WebGL compatibility.
                        // My previous scale(-1, 1) created a 180-degree rotation! We need scale(1, -1) to cleanly cancel the WebGL flip.
                        ctx.scale(1, -1);
                        
                        // Draw the specific cropped part of the master texture, centered, and scaled up
                        ctx.drawImage(
                            faceCanvas,
                            left, top, width, height, // Source crop
                            -finalWidth / 2, -finalHeight / 2, finalWidth, finalHeight // Destination (centered and scaled)
                        );
                        
                        const epsBuffer = generateArtworkEPS(exportedCanvas);
                        const exportName = faceId.replace('print-area-', 'face-'); 
                        zip.file(`${productId}-artwork-${exportName}.eps`, epsBuffer);
                    } else if (faceCanvas) {
                        // Fallback 1:1 if no uvBounds object exists
                        const epsBuffer = generateArtworkEPS(faceCanvas);
                        const exportName = faceId.replace('print-area-', 'face-'); 
                        zip.file(`${productId}-artwork-${exportName}.eps`, epsBuffer);
                    } else {
                        // Fallback guarantees exactly 4 files even if nothing is drawn
                        const emptyCanvas = document.createElement('canvas');
                        emptyCanvas.width = 1024;
                        emptyCanvas.height = 1024;
                        const epsBuffer = generateArtworkEPS(emptyCanvas);
                        const exportName = faceId.replace('print-area-', 'face-'); 
                        zip.file(`${productId}-artwork-${exportName}.eps`, epsBuffer);
                    }
                }
            } else if (window._getPrintAreaCanvas) {
                const hasAllFaces = printAreas.some(p => p.id === 'all-faces');

                for (const area of printAreas) {
                    const canvas = window._getPrintAreaCanvas(area.id);
                    if (!canvas) continue;

                    const splitCount = psdConfig.printAreaSplitting?.[area.id];

                    if (splitCount && splitCount > 1) {
                        const faceWidth = Math.floor(canvas.width / splitCount);
                        const faceHeight = canvas.height;
                        for (let f = 0; f < splitCount; f++) {
                            const faceCanvas = document.createElement('canvas');
                            faceCanvas.width = faceWidth;
                            faceCanvas.height = faceHeight;
                            const ctx = faceCanvas.getContext('2d')!;
                            ctx.drawImage(
                                canvas,
                                f * faceWidth, 0, faceWidth, faceHeight,
                                0, 0, faceWidth, faceHeight
                            );

                            const { generateArtworkEPS } = await import('@/utils/artworkEpsGenerator');
                            const epsBuffer = generateArtworkEPS(faceCanvas);
                            zip.file(`${productId}-artwork-face-${f + 1}.eps`, epsBuffer);
                        }
                    } else {
                        if (hasAllFaces && (
                            area.id.includes('face') ||
                            area.id.includes('side') ||
                            area.id === 'front' ||
                            area.id === 'back' ||
                            area.id === 'left' ||
                            area.id === 'right'
                        )) {
                            continue;
                        }

                        const { generateArtworkEPS } = await import('@/utils/artworkEpsGenerator');
                        const epsBuffer = generateArtworkEPS(canvas);
                        zip.file(`${productId}-artwork-${area.id}.eps`, epsBuffer);
                    }
                }
            }

            // Generate ZIP and trigger download
            const zipContent = await zip.generateAsync({ type: 'blob' });

            const url = URL.createObjectURL(zipContent);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${productId}-package-${Date.now()}.zip`;
            link.click();
            URL.revokeObjectURL(url);

            showToast('Package downloaded successfully!', 'success');
        } catch (error: any) {
            console.error('[ZIP] Generation failed:', error);
            showToast(`Package generation failed: ${error.message || 'Unknown error'}`, 'error');
        } finally {
            useConfiguratorStore.getState().updateAllColors(originalColor);
            setIsEPSProcessing(false);
        }
    };

    // Download SVG — composed vector artwork from editor state
    const handleDownloadSVG = async () => {
        if (isSVGProcessing || isRecording) return;

        setIsSVGProcessing(true);
        showToast('Generating SVG...', 'info');

        try {
            const { generateSvgExport, downloadSvg } = await import('@/utils/vectorSvgExporter');
            const state = useConfiguratorStore.getState();

            const activePrintAreaId = state.activePrintAreaId || (state.printAreas.length > 0 ? state.printAreas[0].id : null);
            if (!activePrintAreaId) {
                showToast('No print area selected.', 'error');
                setIsSVGProcessing(false);
                return;
            }

            const areaLogos = state.logoInstances.filter(l => l.printAreaId === activePrintAreaId);
            const areaTexts = state.textInstances.filter(t => t.printAreaId === activePrintAreaId);

            const activePrintArea = state.printAreas.find(pa => pa.id === activePrintAreaId);
            const canvasSize = 1024; // Match texture canvas size

            const svgContent = generateSvgExport({
                canvasWidth: canvasSize,
                canvasHeight: canvasSize,
                logoInstances: areaLogos,
                textInstances: areaTexts,
                logos: state.logos,
                backgroundColor: activePrintArea?.backgroundColor || undefined,
            });

            const productId = state.currentModel;
            downloadSvg(svgContent, `${productId}-artwork-${activePrintAreaId}-${Date.now()}.svg`);
            showToast('SVG downloaded!', 'success');
        } catch (error: any) {
            console.error('[SVG] Export failed:', error);
            showToast(`SVG export failed: ${error.message || 'Unknown error'}`, 'error');
        } finally {
            setIsSVGProcessing(false);
        }
    };

    // Download Source EPS — pass-through of the original uploaded EPS file
    const handleDownloadSourceEPS = async () => {
        const state = useConfiguratorStore.getState();
        const { downloadSourceFile } = await import('@/utils/vectorSvgExporter');

        // Find logos that have an original EPS source
        const epsLogos = state.logos.filter(l => l.sourceType === 'eps' && l.sourceFile);

        if (epsLogos.length === 0) {
            showToast('No source EPS files available. Upload an EPS file first.', 'info');
            return;
        }

        // Download each source EPS
        for (const logo of epsLogos) {
            downloadSourceFile(logo.sourceFile!, logo.sourceFileName || `${logo.name}.eps`);
        }

        showToast(`Downloaded ${epsLogos.length} source EPS file(s).`, 'success');
    };

    // Poll for recording progress
    useEffect(() => {
        if (!isRecording) return;

        const interval = setInterval(() => {
            if (window._sceneRecordProgress !== undefined) {
                setRecordingProgress(window._sceneRecordProgress);
            }
        }, 100);

        return () => clearInterval(interval);
    }, [isRecording]);

    // Keyboard Shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Don't trigger if user is typing in an input or recording
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
            if (isRecording) return; // Disable shortcuts during recording

            switch (e.key.toLowerCase()) {
                case 'v': // Select tool
                    setToolMode(toolMode === 'select' ? null : 'select');
                    setActiveLogoInstance(null);
                    setActiveTextInstance(null);
                    break;
                case 'h': // Hand/Pan tool
                    setToolMode(toolMode === 'pan' ? null : 'pan');
                    break;
                case 'r': // Reset view
                    if (window._sceneReset) window._sceneReset();
                    break;
                case '+':
                case '=': // Zoom in
                    if (window._sceneZoomIn) window._sceneZoomIn();
                    break;
                case '-': // Zoom out
                    if (window._sceneZoomOut) window._sceneZoomOut();
                    break;
                case 'z': // Undo (Ctrl+Z)
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        undo();
                    }
                    break;
                case 'y': // Redo (Ctrl+Y)
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        redo();
                    }
                    break;
                case 'b': // Toggle print area borders
                    togglePrintAreaBorders();
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [toolMode, setToolMode, undo, redo, togglePrintAreaBorders, setActiveLogoInstance, setActiveTextInstance, isRecording]);

    // Zoom handlers
    const handleZoomIn = () => {
        if (window._sceneZoomIn) window._sceneZoomIn();
    };

    const handleZoomOut = () => {
        if (window._sceneZoomOut) window._sceneZoomOut();
    };

    const handleResetView = () => {
        if (window._sceneReset) window._sceneReset();
    };

    const handleHdriChange = (preset: string) => {
        setLightingPreset(preset);
    };

    return (
        <>
            <div className="floating-toolbar">
                {/* Group 1: Environment (Lighting) */}
                <div className="toolbar-group">
                    <div className="toolbar-custom-component">
                        <HDRISelector
                            currentPreset={lightingPreset}
                            onPresetChange={handleHdriChange}
                            currentBackground={sceneBackground}
                            onBackgroundChange={setSceneBackground}
                        />
                    </div>
                </div>

                {/* Group 2: Selection & History */}
                <div className="toolbar-group">
                    <button
                        className={`toolbar-btn ${toolMode === 'select' ? 'active' : ''}`}
                        onClick={() => setToolMode(toolMode === 'select' ? null : 'select')}
                        title="Select (V)"
                    >
                        <MousePointer2 size={20} />
                    </button>
                    <button
                        className={`toolbar-btn ${toolMode === 'pan' ? 'active' : ''}`}
                        onClick={() => setToolMode(toolMode === 'pan' ? null : 'pan')}
                        title="Pan (H)"
                    >
                        <Hand size={20} />
                    </button>
                    <button
                        className="toolbar-btn"
                        onClick={undo}
                        disabled={!canUndo()}
                        title="Undo"
                    >
                        <Undo size={20} />
                    </button>
                    <button
                        className="toolbar-btn"
                        onClick={redo}
                        disabled={!canRedo()}
                        title="Redo"
                    >
                        <Redo size={20} />
                    </button>
                </div>

                {/* Group 3: Zoom & View */}
                <div className="toolbar-group">
                    <button
                        className="toolbar-btn"
                        onClick={handleZoomIn}
                        title="Zoom In"
                        disabled={!canZoomIn}
                    >
                        <ZoomIn size={20} />
                    </button>
                    <button
                        className="toolbar-btn"
                        onClick={handleZoomOut}
                        title="Zoom Out"
                        disabled={!canZoomOut}
                    >
                        <ZoomOut size={20} />
                    </button>
                    <button className="toolbar-btn" onClick={handleResetView} title="Reset View">
                        <RotateCcw size={20} />
                    </button>
                    <button
                        className={`toolbar-btn ${showPrintAreaBorders ? 'active' : ''}`}
                        onClick={togglePrintAreaBorders}
                        title="Toggle Print Area Borders"
                    >
                        {showPrintAreaBorders ? <Eye size={20} /> : <EyeOff size={20} />}
                    </button>
                </div>

                {/* Group 4: Actions */}
                <div className="toolbar-group">
                    <button className="toolbar-btn" onClick={handleSavePNG} title="Save PNG">
                        <Save size={20} />
                    </button>
                    <button
                        className="toolbar-btn"
                        onClick={handleRecordVideo}
                        title={!glbUrl ? "Model loading..." : "Save Video"}
                        disabled={isProcessing || !glbUrl}
                    >
                        {isProcessing ? (
                            <Loader2 size={20} className="animate-spin" />
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 41 42" fill="none">
                                <path d="M9.5 1.5V11.5H25.5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M37.5 16.5V11.5L27.5 1.5H5.5C4.43913 1.5 3.42172 1.92143 2.67157 2.67157C1.92143 3.42172 1.5 4.43913 1.5 5.5V33.5C1.5 34.5609 1.92143 35.5783 2.67157 36.3284C3.42172 37.0786 4.43913 37.5 5.5 37.5H15" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M28.5 40.5C34.5751 40.5 39.5 35.5751 39.5 29.5C39.5 23.4249 34.5751 18.5 28.5 18.5C22.4249 18.5 17.5 23.4249 17.5 29.5C17.5 35.5751 22.4249 40.5 28.5 40.5Z" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M26 25L33.5 29.5L26 34V25Z" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )}
                    </button>
                    {currentModel === 'square-pen' && (
                        <>
                            <button
                                className="toolbar-btn"
                                onClick={handleDownloadPSD}
                                title="Download PSD"
                                disabled={isPSDProcessing || !glbUrl || isProcessing}
                            >
                                {isPSDProcessing ? (
                                    <Loader2 size={20} className="animate-spin" />
                                ) : (
                                    <img src="/images/SVG/Download PSD.svg" alt="Download PSD" width={20} height={20} />
                                )}
                            </button>
                            <button
                                className="toolbar-btn"
                                onClick={handleDownloadEPS}
                                title="Download EPS"
                                disabled={isEPSProcessing || isPSDProcessing || !glbUrl || isProcessing}
                            >
                                {isEPSProcessing ? (
                                    <Loader2 size={20} className="animate-spin" />
                                ) : (
                                    <img src="/images/SVG/Download EPS.svg" alt="Download EPS" width={20} height={20} />
                                )}
                            </button>
                            <button
                                className="toolbar-btn"
                                onClick={handleDownloadProof}
                                title="Download Template Proof"
                                disabled={isProcessing || !glbUrl || isPSDProcessing || isEPSProcessing}
                            >
                                {isProcessing && !isPSDProcessing && !isEPSProcessing && !isRecording ? (
                                    <Loader2 size={20} className="animate-spin" />
                                ) : (
                                    <img src="/images/SVG/Downlaod Template.svg" alt="Download Template" width={20} height={20} />
                                )}
                            </button>
                        </>
                    )}
                    {/* Download SVG — always available */}
                    <button
                        className="toolbar-btn"
                        onClick={handleDownloadSVG}
                        title="Download SVG (Vector Artwork)"
                        disabled={isSVGProcessing || isPSDProcessing || isEPSProcessing || isRecording || !glbUrl}
                    >
                        {isSVGProcessing ? (
                            <Loader2 size={20} className="animate-spin" />
                        ) : (
                            <FileDown size={20} />
                        )}
                    </button>
                    {/* Download Source EPS — only when EPS logos have been uploaded */}
                    <button
                        className="toolbar-btn"
                        onClick={handleDownloadSourceEPS}
                        title="Download Source EPS (Original uploaded files)"
                    >
                        <img src="/images/SVG/Download EPS.svg" alt="Source EPS" width={20} height={20} style={{ opacity: 0.7 }} />
                    </button>
                    <button className="toolbar-btn" onClick={() => setShowShare(true)} title="Share">
                        <Share2 size={20} />
                    </button>
                </div>
            </div>

            {showShare && <ShareModal onClose={() => setShowShare(false)} />}

            {videoPreview && (
                <VideoPreviewModal 
                    blob={videoPreview.blob} 
                    videoUrl={videoPreview.videoUrl}
                    productName={currentModel}
                    onClose={() => setVideoPreview(null)}
                />
            )}

            {/* Recording Overlay - Shows during video capture */}
            <RecordingOverlay isRecording={isRecording} progress={recordingProgress} />
        </>
    );
}
